import { graphql } from '@gql';

export const GET_SINGLE_NOTIFICATION_NAME_ONLY = graphql(`
    query GetNotificationName($notificationId: ID!) {
        notifications(where: { id: $notificationId }) {
            edges {
                node {
                    title
                }
            }
        }
    }
`);

export const GET_GENERIC_SECURITIY_NOTIFICATION = graphql(`
    query GetGenericSecurityNotification($input: NotificationWhereInput!) {
        notifications(where: $input) {
            edges {
                node {
                    id
                    title
                    publishDate
                    lastUpdateDate
                    priority
                    impact
                    cves {
                        id
                        code
                    }
                    description
                    cvssVector
                    cvss {
                        score
                    }
                    customizedCVSS {
                        score
                    }
                    solutionStatus
                    action
                    solutionDetails
                    references
                    legalNotice
                    vendorAdvisories {
                        vendor
                        name
                        url
                    }
                    components {
                        id
                        name
                        cpeName
                        version
                        componentVendor {
                            name
                        }
                    }
                }
            }
        }
    }
`);

export const GET_SECURITY_NOTIFICATION = graphql(`
    query GetSecurityNotification($id: ID!) {
        notifications(where: { id: $id }) {
            edges {
                node {
                    ...NotificationFragment
                }
            }
        }
    }
`);

export const GET_SECURITY_NOTIFICATIONS_FOR_COMPONENTS = graphql(`
    query GetSecurityNotificationForComponents($ids: [ID!]) {
        notifications(where: { hasComponentsWith: [{ idIn: $ids }] }) {
            edges {
                node {
                    ...NotificationFragment
                }
            }
        }
    }
`);

export const GET_SECURITY_NOTIFICATIONS_FOR_COMPONENT = graphql(`
    query GetSecurityNotificationForComponent($id: ID!) {
        notifications(where: { hasComponentsWith: { id: $id } }) {
            edges {
                node {
                    ...NotificationFragment
                }
            }
        }
    }
`);

export const GET_SECURITY_NOTIFICATIONS_FOR_PROJECT_SERVER = graphql(`
    query GetSecurityNotificationForProjectServer(
        $pid: ID!
        $orderBy: NotificationOrder
        $where: NotificationWhereInput
        $first: Int
        $before: Cursor
        $after: Cursor
    ) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    id
                    name
                    __typename
                    notifications(
                        before: $before
                        after: $after
                        where: $where
                        orderBy: $orderBy
                        first: $first
                        resolved: false
                    ) {
                        totalCount
                        pageInfo {
                            startCursor
                            endCursor
                        }
                        edges {
                            cursor
                            node {
                                ...NotificationFragment
                                affectedComponentsCount
                                affectedAssetItemsCount
                                components {
                                    id
                                    name
                                    cpeName
                                    version
                                    componentVendor {
                                        name
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`);

export const GET_SECURITY_NOTIFICATIONS_SERVER = graphql(`
    query GetSecurityNotificationsServer(
        $orderBy: NotificationOrder
        $where: NotificationWhereInput
        $first: Int
        $before: Cursor
        $after: Cursor
        $search: String
    ) {
        notifications(
            before: $before
            after: $after
            where: $where
            orderBy: $orderBy
            first: $first
            search: $search
        ) {
            totalCount
            pageInfo {
                startCursor
                endCursor
            }
            edges {
                cursor
                node {
                    ...NotificationFragment
                    affectedComponentsCount
                    affectedAssetItemsCount
                    affectedAssetsCount
                    components {
                        id
                        name
                        cpeName
                        version
                        componentVendor {
                            name
                        }
                    }
                }
            }
        }
    }
`);

export const GET_COMPONENT_NOTIFICATIONS_FOR_PROJECT = graphql(`
    extend type ProjectComponentNotification
        @typePolicy(keyFields: "assetItemID notificationID componentID")
    query getProjectComponentsNotifications(
        $pid: ID!
        $where: ProjectComponentNotificationWhereInput
        $orderBy: ProjectComponentNotificationOrder
    ) {
        projectComponentsNotifications(
            projectID: $pid
            where: $where
            orderBy: $orderBy
            first: 100
        ) {
            edges {
                node {
                    id
                    notificationID
                    notificationTitle
                    notificationPriority
                    notificationPublishDate
                    notificationLastUpdateDate
                    notificationCvssBaseScore
                    notificationCvssTemporalScore
                    notificationCvssOveralScore
                    notificationCvssVector
                    notificationImpact
                    notificationSource
                    componentID
                    componentName
                    componentVersion
                    componentIsValid
                    componentEolReached
                    componentCpeName
                    componentVendorID
                    componentVendorName
                    assetItemID
                    assetItemName
                    assetID
                    assetName
                    assetAlias
                    assetStatus
                    assetRating
                    assetTypeID
                    assetTypeName
                    projectZoneID
                    projectZoneName
                    projectZoneExposureRating
                    projectID
                    mitigationID
                    mitigationStatus
                    mitigationAssignee
                    measureID
                    measureName
                    measureType
                    mitigationTestID
                    mitigationTestResult
                    customizedCVSS {
                        score
                        vector
                    }
                }
            }
        }
    }
`);

export const GET_COMPONENT_NOTIFICATIONS_FOR_PROJECT_SERVER = graphql(`
    extend type ProjectComponentNotification
        @typePolicy(keyFields: "assetItemID notificationID componentID")
    query getProjectComponentsNotificationsServer(
        $pid: ID!
        $first: Int
        $where: ProjectComponentNotificationWhereInput
        $orderBy: ProjectComponentNotificationOrder
    ) {
        projectComponentsNotifications(
            projectID: $pid
            where: $where
            orderBy: $orderBy
            first: $first
        ) {
            edges {
                node {
                    id
                    notificationID
                    notificationTitle
                    notificationPriority
                    notificationPublishDate
                    notificationLastUpdateDate
                    notificationCvssBaseScore
                    notificationCvssTemporalScore
                    notificationCvssOveralScore
                    notificationCvssVector
                    notificationImpact
                    notificationSource
                    componentID
                    componentName
                    componentVersion
                    componentIsValid
                    componentEolReached
                    componentCpeName
                    componentVendorID
                    componentVendorName
                    assetItemID
                    assetItemName
                    assetID
                    assetName
                    assetAlias
                    assetStatus
                    assetRating
                    assetTypeID
                    assetTypeName
                    projectZoneID
                    projectZoneName
                    projectZoneExposureRating
                    projectID
                    mitigationID
                    mitigationStatus
                    mitigationAssignee
                    measureID
                    measureName
                    measureType
                    mitigationTestID
                    mitigationTestResult
                    customizedCVSS {
                        score
                        vector
                    }
                }
            }
            totalCount
        }
    }
`);

export const GET_UNRESOLVED_NOTIFICATIONS_BY_ZONE = graphql(`
    extend type ProjectComponentNotification
        @typePolicy(keyFields: "assetItemID notificationID componentID")
    query GetUnresolvedNotificationsByZone(
        $pid: ID!
        $zoneID: ID!
        $first: Int
    ) {
        projectComponentsNotifications(
            projectID: $pid
            where: {
                projectZoneID: $zoneID
                and: [
                    {
                        or: [
                            { mitigationIDIsNil: true }
                            {
                                mitigationIDIsNil: false
                                mitigationStatusNotIn: ["COMPLETED", "OBSOLETE"]
                            }
                        ]
                    }
                    { assetStatusNotIn: ["INVALID"] }
                ]
            }
            first: $first
        ) {
            edges {
                node {
                    assetItemID
                    componentID
                    projectZoneID
                    notificationID
                    mitigationStatus
                }
            }
            totalCount
        }
    }
`);

// Default value for first parameter
export const UNRESOLVED_NOTIFICATIONS_FIRST_DEFAULT = 100;

export const GET_MEASURES_FOR_PROJECT = graphql(`
    query getMeasuresForProject($pid: ID!) {
        measures(where: { projectID: $pid }) {
            edges {
                node {
                    id
                    createTime
                    updateTime
                    name
                    type
                    howto
                    resources
                    justification
                    updateToComponent {
                        id
                        name
                        componentVendor {
                            id
                            name
                        }
                        version
                    }
                    mitigations {
                        id
                    }
                }
            }
            totalCount
        }
    }
`);

export const GET_MEASURES = graphql(`
    query getMeasures($where: MeasureWhereInput!) {
        measures(where: $where) {
            edges {
                node {
                    id
                    createTime
                    updateTime
                    name
                    type
                    howto
                    resources
                    justification
                    updateToComponent {
                        id
                        name
                        componentVendor {
                            id
                            name
                        }
                        version
                    }
                    mitigations {
                        id
                        componentNotification {
                            notification {
                                id
                            }
                        }
                        assetItem {
                            id
                            name
                        }
                        mitigationTests {
                            id
                            updateTime
                        }
                    }
                }
            }
            totalCount
        }
    }
`);

export const GET_MITIGATIONS = graphql(`
    query getMitigations($input: MitigationWhereInput!) {
        mitigations(where: $input) {
            edges {
                node {
                    ...MitigationFragment
                }
            }
            totalCount
        }
    }
`);

export const GET_MITIGATIONS_SERVER = graphql(`
    query getMitigationsServer(
        $where: MitigationWhereInput!
        $first: Int
        $orderBy: MitigationOrder
    ) {
        mitigations(where: $where, first: $first, orderBy: $orderBy) {
            edges {
                node {
                    ...MitigationFragment
                }
            }
            totalCount
        }
    }
`);

export const GET_MEASURES_SERVER = graphql(`
    query getMeasuresServer(
        $where: MeasureWhereInput!
        $first: Int
        $orderBy: MeasureOrder
    ) {
        measures(where: $where, first: $first, orderBy: $orderBy) {
            edges {
                node {
                    id
                    createTime
                    updateTime
                    name
                    type
                    howto
                    resources
                    justification
                    updateToComponent {
                        id
                        name
                        componentVendor {
                            id
                            name
                        }
                        version
                    }
                    mitigations {
                        id
                    }
                }
            }
            totalCount
        }
    }
`);
